import React, { useState } from 'react';
import { Lock, ShieldCheck } from 'lucide-react';
import { aaiApi } from '../../services/aaiApi';
import { useParticipant } from '../../hooks/useParticipant';
import { useAsync } from '../aai/useAsync';
import type { AaiHome, MyTraining, ParticipantHomeData, TrainingModule } from '../../types/aai';
import {
  Action, Banner, Card, Chip, Divided, EmptyState, ErrorNote, Kicker, ListRow, Loading,
  PageBody, PageHead, Panel, PanelHead, RouteMark, StepTrack,
} from '../../components/aa';
import type { TrackStep } from '../../components/aa';
import { HelpDrawer, StepDrawer } from './StepDrawer';

/*
 * Use AI in your work.
 *
 * One next step, the loop it sits in, and the paths it belongs to. The screen
 * answers three questions in that order: what do I do now, how far through the
 * loop is it, and who can see what I do.
 *
 * The privacy card is not decoration. A person practising on their own work
 * needs to know, on the screen where they do it, that their answers are not a
 * management report. If that card ever stops saying so, the promise changed.
 */

// The adoption loop. Five stages, and the numbers on the ones ahead stay
// visible so the remaining distance is readable.
const LOOP: TrackStep[] = [
  { key: 'learn', label: 'Learn' },
  { key: 'practise', label: 'Practise' },
  { key: 'use', label: 'Use in work' },
  { key: 'confirm', label: 'Confirm outcome' },
  { key: 'sustain', label: 'Sustain' },
];

/*
 * Which stage the next step sits in, read from the kind of work it is. This is
 * the one honest signal available: a reading is learning, a homework is
 * practice, a linked step is real work. Confirming an outcome and sustaining
 * it are stages the platform has no per-step signal for yet, so they render as
 * ahead rather than as guessed.
 */
const LOOP_STAGE: Record<string, number> = {
  reading: 0,
  micro_learning: 0,
  quiz: 0,
  homework: 1,
  link: 2,
};

const KIND_CHIP: Record<string, string> = {
  quiz: 'Learning',
  reading: 'Learning',
  micro_learning: 'Learning',
  homework: 'Practice',
  link: 'Applied',
};

// What the person is told about who sees the result, per kind of step. Said in
// the second person because it is a promise to them, not a policy note.
const VISIBILITY: Record<string, string> = {
  quiz: 'Your score stays private',
  reading: 'Only completion is recorded',
  micro_learning: 'Only completion is recorded',
  homework: 'Your response stays private',
  link: 'The run is yours until you share it',
};

const ACTION_LABEL: Record<string, string> = {
  quiz: 'Open path',
  reading: 'Open path',
  micro_learning: 'Open path',
  homework: 'Practise safely',
  link: 'Use in work',
};

const minutesFor = (module: TrainingModule): string => {
  if (module.question_count > 0) return `about ${Math.max(module.question_count * 2, 5)} minutes`;
  return 'a few minutes';
};

const statusOf = (module: TrainingModule): string => {
  if (module.status === 'completed') return 'Complete';
  if (module.kind === 'link') return 'Ready for first use';
  return KIND_CHIP[module.kind] || 'Learning';
};

// Where a step opens. A step with a live linked feature goes there; otherwise
// it goes to the step itself rather than to a route that does not exist yet.
const routeFor = (module: TrainingModule): string => {
  if (module.feature?.live && module.feature.route) return module.feature.route;
  return '/my-training';
};

const NextStep: React.FC<{
  next: TrainingModule;
  position: number;
  total: number;
  onOpen: () => void;
  onHelp: () => void;
}> = ({ next, position, total, onOpen, onHelp }) => {
  const stage = LOOP_STAGE[next.kind] ?? 0;
  const linked = next.feature?.label || 'Not linked to a project yet';
  return (
    <Panel pad="lg">
      <div className="flex items-start justify-between gap-3">
        <Kicker>Next step</Kicker>
        <Chip>{KIND_CHIP[next.kind] || 'Learning'}</Chip>
      </div>

      <h2 className="mt-2 text-[24px] font-bold leading-[33.2px] tracking-[-0.01em] text-ink">{next.title}</h2>
      <p className="m-0 mt-1 text-[13px] leading-[21.5px] text-ink-muted">
        Step {position} of {total} · {minutesFor(next)}
      </p>

      <Card className="mt-5" pad="md">
        <StepTrack steps={LOOP} current={stage} />
      </Card>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <Card pad="md">
          <Kicker tone="muted">Linked project</Kicker>
          <p className="m-0 mt-1.5 text-[13px] leading-[21.5px] text-ink">{linked}</p>
        </Card>
        <Card pad="md">
          <Kicker tone="muted">Visibility</Kicker>
          <p className="m-0 mt-1.5 text-[13px] leading-[21.5px] text-ink">
            {VISIBILITY[next.kind] || 'Only completion is recorded'}
          </p>
        </Card>
      </div>

      {next.description && (
        <p className="m-0 mt-5 text-[13px] leading-[21.5px] text-ink-dim">{next.description}</p>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Action size="lg" onClick={onOpen}>Continue</Action>
        <Action size="lg" variant="quiet" onClick={onHelp}>Get help</Action>
      </div>
    </Panel>
  );
};

const YourView: React.FC<{ role: string }> = ({ role }) => (
  <Card pad="lg" className="h-full">
    <span className="grid h-7 w-7 place-items-center text-ink">
      <ShieldCheck size={20} strokeWidth={1.75} />
    </span>
    <Kicker className="mt-3">Your view</Kicker>
    <p className="m-0 mt-1 text-[18px] font-bold leading-[25.4px] text-ink">{role}</p>
    <p className="m-0 mt-2 text-[13px] leading-[21.5px] text-ink-dim">
      Your practice answers and reflections stay private. Managers see protected completion only.
    </p>
  </Card>
);

const PathRow: React.FC<{ module: TrainingModule; onOpen: () => void }> = ({ module, onOpen }) => (
  <ListRow
    title={module.title}
    foot={`${module.phase || KIND_CHIP[module.kind] || 'Foundation'} · ${module.feature?.label || 'No linked project yet'}`}
    right={(
      <div className="flex items-center gap-4">
        <Chip>{statusOf(module)}</Chip>
        <Action variant="quiet" onClick={onOpen}>{ACTION_LABEL[module.kind] || 'Open path'}</Action>
      </div>
    )}
  />
);

/*
 * Locked until the readiness baseline exists. The same gate the training tab
 * has always had: the server reports readiness_complete, and until it is true
 * there are no steps to show, only the door to the readiness conversation.
 */
const OnboardingGate: React.FC<{ department: string | null | undefined }> = ({ department }) => (
  <PageBody>
    <PageHead
      eyebrow={department || 'AI adoption'}
      title="Use AI in your work"
      sub="Your adoption path unlocks after a short readiness conversation with Orbit."
    />
    <Banner
      size="lg"
      mark={<RouteMark size="xl" className="bg-canvas-inset text-ink" icon={<Lock size={22} strokeWidth={2} />} />}
      kicker="Onboarding"
      title="Your AI adoption unlocks after your readiness check"
      desc="Complete the quick readiness conversation with Orbit and the right steps line up for you. Your answers stay yours; managers only ever see protected aggregates."
      action={<Action size="lg" arrow to="/onboarding">Complete my readiness check</Action>}
    />
  </PageBody>
);

const ROLE_VIEW: Record<string, string> = {
  AI_PARTICIPANT: 'Employee',
  DEPARTMENT_HEAD: 'Department head',
  AI_CHAMPION: 'AI champion',
  OWNER: 'Owner',
  TRAINER: 'Trainer',
  EXPERT: 'Expert',
};

const AdoptionPage: React.FC = () => {
  const { role } = useParticipant();
  // Which step is open over the page, and whether it opened asking for help.
  const [openStep, setOpenStep] = useState<TrainingModule | null>(null);
  const [helpFor, setHelpFor] = useState<TrainingModule | null>(null);
  const home = useAsync<AaiHome>(() => aaiApi.getHome(), 'Could not load your adoption view.');
  const training = useAsync<MyTraining>(() => aaiApi.getMyTraining(), 'Could not load your training.');

  if (home.loading || training.loading) {
    return <PageBody><Loading label="Loading your adoption view" /></PageBody>;
  }
  if (training.error || !training.data) {
    return <PageBody><ErrorNote message={training.error || 'Something went wrong.'} /></PageBody>;
  }

  const participant = home.data as ParticipantHomeData | null;
  if (!training.data.readiness_complete) return <OnboardingGate department={participant?.department} />;
  const modules = training.data.modules || [];
  const next = modules.find((m) => m.status !== 'completed');
  const total = training.data.summary?.total || modules.length;

  return (
    <PageBody>
      <PageHead
        eyebrow={participant?.department || 'AI adoption'}
        title="Use AI in your work"
        sub="Complete the next step, practise safely, then use an approved Project in your work."
      />

      <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
        {next && (
          <NextStep
            next={next}
            position={modules.indexOf(next) + 1}
            total={total}
            onOpen={() => setOpenStep(next)}
            onHelp={() => setHelpFor(next)}
          />
        )}
        {!next && (
          <Panel>
            <EmptyState
              title="You are up to date"
              reason="Every step assigned to you is complete. A new one appears here when your program adds it, and everything you covered is listed below."
            />
          </Panel>
        )}
        <YourView role={ROLE_VIEW[role || ''] || 'Member'} />
      </div>

      <Panel className="mt-5">
        <PanelHead
          kicker="Current work"
          title="Your paths"
          action={<span className="text-[13px] leading-[21.5px] text-ink-dim">{modules.length} items</span>}
        />
        {modules.length === 0 && (
          <EmptyState
            title="No paths assigned yet"
            reason="A path appears here once your program assigns one to you or your department."
          />
        )}
        <Divided>
          {modules.map((m) => <PathRow key={m.id} module={m} onOpen={() => setOpenStep(m)} />)}
        </Divided>
      </Panel>

      {openStep && (
        <StepDrawer
          module={openStep}
          onClose={() => setOpenStep(null)}
          onCompleted={training.reload}
        />
      )}
      {helpFor && <HelpDrawer stepTitle={helpFor.title} onClose={() => setHelpFor(null)} />}
    </PageBody>
  );
};

export default AdoptionPage;
